import {
  Badge,
  ComponentMultiStyleConfig,
  Flex,
  Text,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import React, { ReactElement, ReactNode, useMemo } from "react";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import { hasValue, isNullish } from "../../../../lib/util/common-util";
import { DeleteIconButton } from "../../../icon-button";
import { CMButtonFormCommentProps } from "../../comment";
import { CMFormControlHeader } from "../../control";

export type CMFrameCardProps = {
  title?: string;
  deleteButtonIcon?: ReactElement;
  onDelete?: () => void;
  backgroundColor?: string;

  /**
   * 画面表示モード
   */
  displayMode?: "normal" | "added" | "updated" | "deleted";
  /**
   * 画面表示モードに応じた表示メッセージ
   */
  displayModeMessage?: {
    added?: string;
    updated?: string;
    deleted?: string;
  };

  /**
   * コメントボタンのprops。
   * これが存在すれば、コメントボタンを表示する。
   */
  commentButtonProps?: CMButtonFormCommentProps;

  children?: ReactNode;
} & ComponentStyleProps;

/**
 * 申請書等に表示されるカードのフレームを表示するコンポーネント
 */
export const CMFrameCard = ({
  title,
  deleteButtonIcon,
  onDelete,
  backgroundColor = "gray.100",

  displayMode = "normal",
  displayModeMessage,

  commentButtonProps,

  children,

  sx,
  ...rest
}: CMFrameCardProps) => {
  const styles = useMultiStyleConfig("CMFrameCardStyles");

  const containerStyles = useMemo(
    () => ({
      ...styles.container,
      ...(displayMode === "added" ? styles.modeAdd : undefined),
      ...(displayMode === "updated" ? styles.modeUpdate : undefined),
      ...(displayMode === "deleted" ? styles.modeDelete : undefined),
      ...sx,
    }),
    [styles, displayMode, sx],
  );

  const statusMessage = useMemo(() => {
    if (isNullish(displayModeMessage)) return undefined;
    switch (displayMode) {
      case "added":
        return displayModeMessage.added;
      case "updated":
        return displayModeMessage.updated;
      case "deleted":
        return displayModeMessage.deleted;
      default:
        return undefined;
    }
  }, [displayMode, displayModeMessage]);

  return (
    <VStack bgColor={backgroundColor} sx={containerStyles} {...rest}>
      <Flex sx={styles.header}>
        <Text sx={styles.title}>{title}</Text>

        {/*変更ステータスラベル*/}
        {hasValue(statusMessage) && (
          <Badge sx={styles.stateBadge}>{statusMessage}</Badge>
        )}

        {/*コメントボタン*/}
        {hasValue(commentButtonProps) && (
          <CMFormControlHeader<string>
            value={null}
            commentButtonProps={commentButtonProps}
          />
        )}

        {deleteButtonIcon ? (
          <DeleteIconButton
            icon={deleteButtonIcon}
            size="sm"
            ariaLabel="Delete card"
            onClick={onDelete}
            sx={styles.deleteButton}
          />
        ) : null}
      </Flex>
      {hasValue(children) ? (
        <VStack sx={styles.content}>{children}</VStack>
      ) : undefined}
    </VStack>
  );
};

export const CMFrameCardStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "header",
    "title",
    "tipMessage",
    "deleteButton",
    "content",
    "modeAdd",
    "modeUpdate",
    "modeDelete",
    "stateBadge",
  ],
  baseStyle: {
    container: {
      pt: "12px",
      pr: "3",
      pb: "5",
      pl: "3",

      borderWidth: "1px",
      borderColor: "gray.400",
      borderRadius: "6px",

      fontSize: "sm",
    },
    header: {
      alignSelf: "stretch",
    },
    title: {
      fontSize: "md",
      fontWeight: "bold",
      color: "gray.900",
      minW: "40px",
      maxW: "500px",
    },
    tipMessage: {
      ml: "2",

      fontSize: "xs",
      color: "gray.600",
    },
    deleteButton: {
      ml: "auto",
      color: "gray.400",
    },
    content: {
      alignSelf: "stretch",

      fontSize: "md",
      color: "gray.900",
    },

    modeAdd: {
      borderWidth: "2px",
      borderColor: "orange.500",
    },
    modeUpdate: {
      borderWidth: "2px",
      borderColor: "orange.500",
    },
    modeDelete: {
      backgroundColor: "gray.500",
    },
    stateBadge: {
      ml: "8px",
      pt: "1px",
      borderWidth: "1px",
      borderColor: "gray.500",
      bgColor: "transparent",
    },
  },
};
